import { useState } from 'react';
import type {
  MarketConfigDTO,
  MarketConfigUpdateParams,
  MarketWithdrawalChannelDTO,
  MarketWithdrawalConfigDTO,
  MarketWithdrawalFixedAmountDTO,
  MarketWithdrawalRuntimeMode,
} from '../../features/settings/types/settings.types';
import { ALL_TASK_TYPES } from '../../features/tasks/types/tasks.types';

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-400';
const selectCls =
  'h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-400';

const RUNTIME_OPTIONS: Array<{ value: MarketWithdrawalRuntimeMode; label: string }> = [
  { value: 'disabled', label: '关闭自动打款' },
  { value: 'test', label: '测试模式' },
  { value: 'live', label: '正式打款' },
];

function defaultWithdrawalConfig(
  context: Pick<MarketConfigUpdateParams, 'currency' | 'currency_exponent'>,
): MarketWithdrawalConfigDTO {
  return {
    withdrawal_enabled: false,
    provider: 'csjvnpay',
    runtime_mode: 'disabled',
    currency: context.currency || 'VND',
    currency_exponent: Number(context.currency_exponent) || 0,
    min_amount_minor: 0,
    max_amount_minor: 0,
    fixed_amounts: [],
    channels: [
      {
        channel: 'bank_card',
        label: 'CSJVNPay 越南盾通道',
        provider: 'csjvnpay',
        enabled: true,
        sort_order: 1,
      },
    ],
    app_id: '',
    secret: '',
    base_url: '',
    notify_path: '/v1/webhooks/csjvnpay/payout',
    merchant_private_key: '',
    platform_public_key: '',
    app_id_configured: false,
    secret_configured: false,
    base_url_configured: false,
    merchant_private_key_configured: false,
    platform_public_key_configured: false,
    runtime_credential_ready: false,
  };
}
function formatAmountInput(amountMinor: number, exponent: number): string {
  if (exponent <= 0) return String(Math.max(0, Math.trunc(amountMinor || 0)));
  return (Math.max(0, amountMinor || 0) / Math.pow(10, exponent)).toFixed(exponent);
}

function parseAmountInput(value: string, exponent: number): number {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) return 0;
  if (exponent <= 0) return Math.round(amount);
  return Math.round(amount * Math.pow(10, exponent));
}

function normalizeMarketWithdrawalConfig(
  config: MarketWithdrawalConfigDTO | undefined,
  context: Pick<MarketConfigUpdateParams, 'currency' | 'currency_exponent'>,
  forSave = false,
): MarketWithdrawalConfigDTO {
  const base = config ?? defaultWithdrawalConfig(context);
  const exponent = Math.max(0, Math.trunc(Number(base.currency_exponent ?? context.currency_exponent) || 0));
  const channels = (base.channels?.length ? base.channels : defaultWithdrawalConfig(context).channels)
    .map((channel, index) => ({
      channel: channel.channel.trim() || `channel_${index + 1}`,
      label: channel.label.trim() || 'CSJVNPay',
      provider: 'csjvnpay' as const,
      enabled: Boolean(channel.enabled),
      sort_order: Number(channel.sort_order) || index + 1,
    }))
    .sort((left, right) => left.sort_order - right.sort_order);
  const fixed_amounts = (base.fixed_amounts ?? [])
    .map((amount, index) => ({
      amount_minor: Math.max(0, Math.trunc(Number(amount.amount_minor) || 0)),
      label: amount.label.trim(),
      enabled: Boolean(amount.enabled),
      sort_order: Number(amount.sort_order) || index + 1,
    }))
    .filter((amount) => !forSave || amount.amount_minor > 0)
    .sort((left, right) => left.sort_order - right.sort_order);
  return {
    withdrawal_enabled: Boolean(base.withdrawal_enabled),
    provider: 'csjvnpay',
    runtime_mode: RUNTIME_OPTIONS.some((option) => option.value === base.runtime_mode)
      ? base.runtime_mode
      : 'disabled',
    currency: (base.currency || context.currency || 'VND').toUpperCase(),
    currency_exponent: exponent,
    min_amount_minor: Math.max(0, Math.trunc(Number(base.min_amount_minor) || 0)),
    max_amount_minor: Math.max(0, Math.trunc(Number(base.max_amount_minor) || 0)),
    fixed_amounts,
    channels,
    app_id: (base.app_id ?? '').trim(),
    secret: (base.secret ?? '').trim(),
    base_url: (base.base_url ?? '').trim().replace(/\/+$/, ''),
    notify_path: (base.notify_path ?? '/v1/webhooks/csjvnpay/payout').trim() || '/v1/webhooks/csjvnpay/payout',
    merchant_private_key: (base.merchant_private_key ?? '').trim(),
    platform_public_key: (base.platform_public_key ?? '').trim(),
    app_id_configured: Boolean(base.app_id_configured || (base.app_id ?? '').trim()),
    secret_configured: Boolean(base.secret_configured),
    base_url_configured: Boolean(base.base_url_configured || (base.base_url ?? '').trim()),
    merchant_private_key_configured: Boolean(base.merchant_private_key_configured),
    platform_public_key_configured: Boolean(base.platform_public_key_configured),
    runtime_credential_ready: Boolean(base.runtime_credential_ready),
  };
}

function buildInitialForm(item: MarketConfigDTO): MarketConfigUpdateParams {
  return {
    name: item.name,
    locale: item.locale,
    currency: item.currency,
    currency_symbol: item.currency_symbol,
    currency_exponent: item.currency_exponent,
    enabled_task_types: item.enabled_task_types ?? [],
    enabled_features: item.enabled_features ?? {},
    country_confirmation_required: item.country_confirmation_required,
    task_configs: item.task_configs ?? [],
    withdrawal_config: item.market_id === 'VN'
      ? normalizeMarketWithdrawalConfig(item.withdrawal_config, item)
      : item.withdrawal_config,
  };
}

function normalizeTaskConfigs(form: MarketConfigUpdateParams): MarketConfigUpdateParams {
  const enabled = new Set(form.enabled_task_types);
  const existing = new Map(form.task_configs.map((item) => [item.task_type, item]));
  const task_configs = ALL_TASK_TYPES.map((taskType) => {
    const current = existing.get(taskType);
    return {
      task_type: taskType,
      enabled: enabled.has(taskType),
      reward_amount_minor: Math.max(0, Math.trunc(current?.reward_amount_minor ?? 0)),
    };
  });
  return {
    ...form,
    task_configs,
    withdrawal_config: form.withdrawal_config
      ? normalizeMarketWithdrawalConfig(form.withdrawal_config, form, true)
      : undefined,
  };
}

export function MarketCard({
  item,
  saving,
  onSave,
  mode = 'market',
}: {
  mode?: 'market' | 'payout';
  item: MarketConfigDTO;
  saving: boolean;
  onSave: (marketId: string, params: MarketConfigUpdateParams) => void;
}) {
  const [form, setForm] = useState<MarketConfigUpdateParams>(() => buildInitialForm(item));
  const updateCurrency = (currency: string) => {
    setForm((prev) => ({
      ...prev,
      currency: currency.toUpperCase(),
      withdrawal_config: prev.withdrawal_config
        ? { ...prev.withdrawal_config, currency: currency.toUpperCase() }
        : prev.withdrawal_config,
    }));
  };

  const updateExponent = (exponent: number) => {
    setForm((prev) => ({
      ...prev,
      currency_exponent: exponent,
      withdrawal_config: prev.withdrawal_config
        ? { ...prev.withdrawal_config, currency_exponent: exponent }
        : prev.withdrawal_config,
    }));
  };

  const updateWithdrawalConfig = (patch: Partial<MarketWithdrawalConfigDTO>) => {
    setForm((prev) => ({
      ...prev,
      withdrawal_config: normalizeMarketWithdrawalConfig({
        ...normalizeMarketWithdrawalConfig(prev.withdrawal_config, prev),
        ...patch,
      }, prev),
    }));
  };

  const updateWithdrawalChannel = (
    index: number,
    key: keyof MarketWithdrawalChannelDTO,
    value: MarketWithdrawalChannelDTO[keyof MarketWithdrawalChannelDTO],
  ) => {
    setForm((prev) => {
      const config = normalizeMarketWithdrawalConfig(prev.withdrawal_config, prev);
      return {
        ...prev,
        withdrawal_config: {
          ...config,
          channels: config.channels.map((channel, channelIndex) => (
            channelIndex === index ? { ...channel, [key]: value } : channel
          )),
        },
      };
    });
  };

  const addWithdrawalFixedAmount = () => {
    setForm((prev) => {
      const config = normalizeMarketWithdrawalConfig(prev.withdrawal_config, prev);
      return {
        ...prev,
        withdrawal_config: {
          ...config,
          fixed_amounts: [
            ...config.fixed_amounts,
            { amount_minor: 0, label: '', enabled: true, sort_order: config.fixed_amounts.length + 1 },
          ],
        },
      };
    });
  };

  const updateWithdrawalFixedAmount = (
    index: number,
    key: keyof MarketWithdrawalFixedAmountDTO,
    value: MarketWithdrawalFixedAmountDTO[keyof MarketWithdrawalFixedAmountDTO],
  ) => {
    setForm((prev) => {
      const config = normalizeMarketWithdrawalConfig(prev.withdrawal_config, prev);
      return {
        ...prev,
        withdrawal_config: {
          ...config,
          fixed_amounts: config.fixed_amounts.map((amount, amountIndex) => (
            amountIndex === index ? { ...amount, [key]: value } : amount
          )),
        },
      };
    });
  };

  const removeWithdrawalFixedAmount = (index: number) => {
    setForm((prev) => {
      const config = normalizeMarketWithdrawalConfig(prev.withdrawal_config, prev);
      return {
        ...prev,
        withdrawal_config: {
          ...config,
          fixed_amounts: config.fixed_amounts.filter((_, amountIndex) => amountIndex !== index),
        },
      };
    });
  };

  const showVNWithdrawalConfig = mode === 'payout';
  const withdrawalConfig = showVNWithdrawalConfig
    ? normalizeMarketWithdrawalConfig(form.withdrawal_config, form)
    : undefined;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{item.market_id} · {form.name}</h2>
          <p className="mt-1 text-sm text-gray-500">
            {form.currency} / {form.locale}，{mode === 'payout' ? '按地区维护提现金额和支付通道。' : '维护地区基础信息。'}
          </p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(item.market_id, normalizeTaskConfigs(form))}
          className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? '保存中...' : '保存'}
        </button>
      </div>

      {mode === 'market' && (<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">市场名称</span>
          <input className={inputCls} value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">语言</span>
          <select className={selectCls} value={form.locale} onChange={(e) => setForm((prev) => ({ ...prev, locale: e.target.value }))}>
            <option value="zh-CN">简体中文</option>
            <option value="vi-VN">Tiếng Việt</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">币种</span>
          <input className={inputCls} value={form.currency} onChange={(e) => updateCurrency(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">币种符号</span>
          <input className={inputCls} value={form.currency_symbol} onChange={(e) => setForm((prev) => ({ ...prev, currency_symbol: e.target.value }))} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">小数位</span>
          <select className={selectCls} value={form.currency_exponent} onChange={(e) => updateExponent(Number(e.target.value))}>
            <option value={0}>0</option>
            <option value={2}>2</option>
          </select>
        </label>
      </div>)}

      {withdrawalConfig && (
        <div className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-4">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">越南提现配置</h3>
              <p className="mt-1 text-sm text-gray-500">
                金额按越南盾（VND）填写。App 仅展示已启用、在最小和最大提现额范围内且有已启用支付通道的档位；请同时开启允许提现。
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
              通道凭据：{withdrawalConfig.runtime_credential_ready ? '已完整配置' : '未完整配置'}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <label className="flex items-center gap-2 pt-6 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={withdrawalConfig.withdrawal_enabled}
                onChange={(e) => updateWithdrawalConfig({ withdrawal_enabled: e.target.checked })}
              />
              允许提现
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">打款运行模式</span>
              <select
                className={selectCls}
                value={withdrawalConfig.runtime_mode}
                onChange={(e) => updateWithdrawalConfig({ runtime_mode: e.target.value as MarketWithdrawalRuntimeMode })}
              >
                {RUNTIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">提现币种</span>
              <input
                className={inputCls}
                value={withdrawalConfig.currency}
                onChange={(e) => updateWithdrawalConfig({ currency: e.target.value.toUpperCase() })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">提现小数位</span>
              <select
                className={selectCls}
                value={withdrawalConfig.currency_exponent}
                onChange={(e) => updateWithdrawalConfig({ currency_exponent: Number(e.target.value) })}
              >
                <option value={0}>0</option>
                <option value={2}>2</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">最小提现额</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{form.currency_symbol}</span>
                <input
                  className={inputCls}
                  type="number"
                  min={0}
                  step={withdrawalConfig.currency_exponent > 0 ? 0.01 : 1}
                  value={formatAmountInput(withdrawalConfig.min_amount_minor, withdrawalConfig.currency_exponent)}
                  onChange={(e) => updateWithdrawalConfig({
                    min_amount_minor: parseAmountInput(e.target.value, withdrawalConfig.currency_exponent),
                  })}
                />
              </div>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">最大提现额</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{form.currency_symbol}</span>
                <input
                  className={inputCls}
                  type="number"
                  min={0}
                  step={withdrawalConfig.currency_exponent > 0 ? 0.01 : 1}
                  value={formatAmountInput(withdrawalConfig.max_amount_minor, withdrawalConfig.currency_exponent)}
                  onChange={(e) => updateWithdrawalConfig({
                    max_amount_minor: parseAmountInput(e.target.value, withdrawalConfig.currency_exponent),
                  })}
                />
              </div>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">商户号</span>
              <input
                className={inputCls}
                value={withdrawalConfig.app_id}
                onChange={(e) => updateWithdrawalConfig({ app_id: e.target.value })}
                placeholder={withdrawalConfig.app_id_configured ? '已配置' : 'CSJVNPay merchno'}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Secret Key</span>
              <input
                className={inputCls}
                value={withdrawalConfig.secret}
                onChange={(e) => updateWithdrawalConfig({ secret: e.target.value })}
                placeholder={withdrawalConfig.secret_configured ? '已配置，留空表示不修改' : '请输入 Secret Key'}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Base URL</span>
              <input
                className={inputCls}
                value={withdrawalConfig.base_url}
                onChange={(e) => updateWithdrawalConfig({ base_url: e.target.value })}
                placeholder={withdrawalConfig.base_url_configured ? '已配置' : 'https://...'}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">回调路径</span>
              <input
                className={inputCls}
                value={withdrawalConfig.notify_path}
                onChange={(e) => updateWithdrawalConfig({ notify_path: e.target.value })}
                placeholder="/v1/webhooks/csjvnpay/payout"
                autoComplete="off"
              />
            </label>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">商户私钥</span>
              <textarea
                value={withdrawalConfig.merchant_private_key}
                onChange={(e) => updateWithdrawalConfig({ merchant_private_key: e.target.value })}
                rows={5}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                placeholder={withdrawalConfig.merchant_private_key_configured ? '已配置，留空表示不修改' : '粘贴商户 RSA 私钥'}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">平台公钥</span>
              <textarea
                value={withdrawalConfig.platform_public_key}
                onChange={(e) => updateWithdrawalConfig({ platform_public_key: e.target.value })}
                rows={5}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                placeholder={withdrawalConfig.platform_public_key_configured ? '已配置，留空表示不修改' : '粘贴 CSJVNPay 平台公钥'}
                autoComplete="off"
              />
            </label>
          </div>

          <div className="mt-5">
            <h4 className="mb-3 text-sm font-semibold text-gray-900">支付通道</h4>
            <div className="grid gap-3 md:grid-cols-2">
              {withdrawalConfig.channels.map((channel, index) => (
                <div key={`${channel.channel}-${index}`} className="rounded-md border border-gray-200 bg-white p-3">
                  <label className="flex items-center justify-between gap-3 text-sm font-medium text-gray-700">
                    <span>CSJVNPay · {channel.label}</span>
                    <input
                      type="checkbox"
                      checked={channel.enabled}
                      onChange={(e) => updateWithdrawalChannel(index, 'enabled', e.target.checked)}
                    />
                  </label>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-gray-500">通道标识</span>
                      <input
                        className={inputCls}
                        value={channel.channel}
                        onChange={(e) => updateWithdrawalChannel(index, 'channel', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-gray-500">展示名称</span>
                      <input
                        className={inputCls}
                        value={channel.label}
                        onChange={(e) => updateWithdrawalChannel(index, 'label', e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h4 className="text-sm font-semibold text-gray-900">固定提现金额</h4>
              <button
                type="button"
                onClick={addWithdrawalFixedAmount}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                添加金额
              </button>
            </div>
            {withdrawalConfig.fixed_amounts.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-400">
                暂无固定提现金额
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2">金额</th>
                      <th className="px-3 py-2">标签</th>
                      <th className="px-3 py-2">启用</th>
                      <th className="px-3 py-2">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {withdrawalConfig.fixed_amounts.map((amount, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{form.currency_symbol}</span>
                            <input
                              className={inputCls}
                              type="number"
                              min={0}
                              step={withdrawalConfig.currency_exponent > 0 ? 0.01 : 1}
                              value={formatAmountInput(amount.amount_minor, withdrawalConfig.currency_exponent)}
                              onChange={(e) => updateWithdrawalFixedAmount(
                                index,
                                'amount_minor',
                                parseAmountInput(e.target.value, withdrawalConfig.currency_exponent),
                              )}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            className={inputCls}
                            value={amount.label}
                            onChange={(e) => updateWithdrawalFixedAmount(index, 'label', e.target.value)}
                            placeholder={`${form.currency_symbol}${formatAmountInput(amount.amount_minor, withdrawalConfig.currency_exponent)}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={amount.enabled}
                            onChange={(e) => updateWithdrawalFixedAmount(index, 'enabled', e.target.checked)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removeWithdrawalFixedAmount(index)}
                            className="text-sm text-red-600 hover:underline"
                          >
                            删除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
